import { useEffect, useState } from 'react';
import { api, errorText } from '../lib/api';
import { cacheGet, cacheSet } from '../lib/store';
import { fmtDateTime, go } from '../lib/util';

export function Campaigns() {
  const [list, setList] = useState<any[] | null>(null);
  const [offline, setOffline] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const load = async () => {
    try {
      const l = await api<any[]>('/api/campaigns');
      setList(l);
      setOffline(false);
      void cacheSet('campaigns', l);
    } catch {
      setOffline(true);
      setList((await cacheGet<any[]>('campaigns')) ?? []);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const active = list?.filter((c) => !c.archived) ?? [];
  const archived = list?.filter((c) => c.archived) ?? [];
  return (
    <div className="page narrow">
      <div className="page-head">
        <h1>Campaigns</h1>
        <button className="btn primary" onClick={() => setShowNew(!showNew)} disabled={offline}>
          New campaign
        </button>
      </div>
      {offline && <p className="notice">Offline — showing the last known list.</p>}
      {showNew && <NewCampaign onCreated={(c) => go(`/c/${c.id}`)} />}
      {list === null ? (
        <p className="muted">Loading…</p>
      ) : active.length === 0 && !showNew ? (
        <div className="empty">
          <p>No campaigns yet.</p>
          <button className="btn primary" onClick={() => setShowNew(true)}>
            Create your first campaign
          </button>
        </div>
      ) : (
        <ul className="card-list">
          {active.map((c) => (
            <li key={c.id}>
              <a className="card campaign-card" href={`#/c/${c.id}`}>
                <div>
                  <h2>{c.name}</h2>
                  <p className="muted">
                    {[c.game_system, c.edition].filter(Boolean).join(' ') || 'No system set'} · {c.my_role === 'gm' ? 'I am the GM' : 'I am a player'}
                  </p>
                </div>
                <p className="muted small">
                  {c.session_count} session{c.session_count === 1 ? '' : 's'}
                  {c.last_entry_at ? ` · last note ${fmtDateTime(c.last_entry_at)}` : ''}
                </p>
              </a>
            </li>
          ))}
        </ul>
      )}
      {archived.length > 0 && (
        <details>
          <summary>Archived ({archived.length})</summary>
          <ul className="card-list">
            {archived.map((c) => (
              <li key={c.id}>
                <a className="card campaign-card" href={`#/c/${c.id}`}>
                  <h2>{c.name}</h2>
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function NewCampaign({ onCreated }: { onCreated: (c: any) => void }) {
  const [f, setF] = useState({ name: '', gameSystem: '', edition: '', myRole: 'player', myName: '' });
  const [err, setErr] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      onCreated(await api('/api/campaigns', { body: { ...f, myName: f.myName || undefined } }));
    } catch (e2) {
      setErr(errorText(e2));
    }
  };
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <form className="card form-grid" onSubmit={submit}>
      <label className="span2">
        Name
        <input value={f.name} onChange={set('name')} required maxLength={120} autoFocus />
      </label>
      <label>
        Game system
        <input value={f.gameSystem} onChange={set('gameSystem')} placeholder="e.g. Call of Cthulhu" />
      </label>
      <label>
        Edition
        <input value={f.edition} onChange={set('edition')} placeholder="e.g. 7e" />
      </label>
      <label>
        My role
        <select value={f.myRole} onChange={set('myRole')}>
          <option value="player">Player</option>
          <option value="gm">GM</option>
        </select>
      </label>
      <label>
        My name at the table
        <input value={f.myName} onChange={set('myName')} placeholder="Me" />
      </label>
      {f.myRole === 'gm' && <p className="muted span2">As GM, new notes default to GM-only. Mark notes public (or set a public prefix) to include them in player-safe recaps.</p>}
      {err && <p className="error span2">{err}</p>}
      <div className="span2">
        <button className="btn primary">Create campaign</button>
      </div>
    </form>
  );
}
